import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { LOG_API_CALL_DURATION_ENABLE } from '@aibees/shared/constants';
import { classnames, peformanceLogTime } from '@aibees/shared/utils';
import { Box, Switch, SwitchProps, Tooltip, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { SwitchFilterProps as Props } from './types';

// INFO: this filter is not generaly used for now, if we want to use it for other usecases we need to change the logic of the filter
export const SwitchFilter = memo(
  ({ className, open, metadata, field, ...filter }: Props) => {
    const { params, setParam, removeParam } = useSearchQueryFilter({
      select: [field!],
    });
    const searchType = useSearchTypeFromRoute();
    const { track } = useTrackEvents();
    const selectedValue = useMemo(
      () => params[field!] ?? 'true',
      [params, field]
    );

    const handleChange: SwitchProps['onChange'] = (
      event: React.ChangeEvent<HTMLInputElement>,
      checked: boolean
    ) => {
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change start: ${field}`);

      // because by default the switch is checked, we need to set the param to false when the switch is unchecked
      if (checked === false) {
        setParam(field!, 'false');
      } else {
        removeParam(field!);
      }

      track('FILTER', {
        filterName: field,
        filterType: filter.type,
        filterValue: checked,
        searchType,
      });
      if (LOG_API_CALL_DURATION_ENABLE)
        peformanceLogTime(`filter change end: ${field}`);
    };

    return (
      <Styled.Wrapper
        className={classnames('SwitchFilter', className)}
        sx={SX.root}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography
              variant="body3"
              color="grey.700"
              fontWeight={400}
              lineHeight={2}
            >
              {filter.name}
            </Typography>
            {metadata?.info && (
              <Tooltip
                title={metadata?.info}
                arrow
                placement="right"
                maxWidth="unset"
                enterDelay={200}
              >
                <Box display="flex" justifyContent="center" alignItems="center">
                  <Icon name="info" size={16} color="grey.400" />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Box display="flex" alignItems="center">
            <Switch
              checked={selectedValue === 'true'}
              onChange={handleChange}
            />
          </Box>
        </Box>
      </Styled.Wrapper>
    );
  }
);

SwitchFilter.displayName = 'SwitchFilter';
